import uploadService from '@/services/embeds/plugin/upload.service'
import editorService from '@/services/ide/editor.service'
import {
  useEmbedConfigureStore,
  type IComponentSettings,
  type IEmbed,
  type IEmbedConfigConig,
  type IEmbedLanguage
} from '@/stores/embeds/embedConfigure.store'
import { useIdeStore } from '@/stores/ide.store'
import { EMBEDTYPES } from '@/utils/embeds/accountMeta'
import { COMPONENTSETTINGS, CONFIG, CSS, HTML, JS } from '@/utils/embeds/defaultConfig'
import { cloneDeep, pick } from 'lodash-es'
import { animals, uniqueNamesGenerator } from 'unique-names-generator'

import { IDECONSTANT } from '@/utils/ide'
import axios from 'axios'

/**
 * Select the parent component to avoid saving the child component bug
 * @returns - promise
 */
const selectParentComponent = async () => {
  try {
    let selectedComp: any | undefined = useEmbedConfigureStore().getEditor?.getSelected() as
      | any
      | undefined
    while (selectedComp) {
      if (selectedComp.parent()) selectedComp = selectedComp.parent()
      else {
        useEmbedConfigureStore().getEditor.select(selectedComp)
        break
      }
      await new Promise((resolve) => setTimeout(resolve, 50))
    }
    await new Promise((resolve) => setTimeout(resolve, 100))
  } catch (error) {
    await new Promise((resolve) => setTimeout(resolve, 100))
    return
  }
}
/**
 * Save the embed config
 * @returns - promise
 */
const saveDraft = async (): Promise<string> => {
  await selectParentComponent()
  const embed: IEmbed = cloneDeep(useEmbedConfigureStore().getEmbed)
  if (embed.draft) {
    embed.activeStatus = true
    embed.savedAsDraft = true
    embed.draft.config = cloneDeep(useEmbedConfigureStore().editorConfig)
  }
  const payload = embed
  return await axios
    .post('/api/embed/updateEmbed', payload)
    .then(() => {
      return 'Embed saved as draft successfully'
    })
    .catch(() => {
      throw new Error('Failed to saved as draft')
    })
}
/**
 * Refresh the editor to fix the freezing issue on save
 */
const renderEditor = async () => {
  useEmbedConfigureStore().getEditor?.render()
}
/**
 * Save the embed config
 * @returns - promise
 */
const saveConfig = async (): Promise<string> => {
  await renderEditor()
  const embed: IEmbed = cloneDeep(useEmbedConfigureStore().getEmbed)
  if (embed.draft) {
    embed.activeStatus = true
    embed.savedAsDraft = null
    embed.draft.config = cloneDeep(useEmbedConfigureStore().editorConfig)
  }
  embed.config = cloneDeep(embed.draft) as IEmbedConfigConig

  const html = useEmbedConfigureStore().htmlRef
  const regex = /<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi
  const cleanedHtml = html.replace(regex, '')
  const css = useEmbedConfigureStore().cssRef
  const js = useEmbedConfigureStore().jsRef
  embed.config.html = cleanedHtml || HTML
  embed.config.css = css || CSS
  embed.config.js = js || JS

  const payload = embed
  return await axios
    .post('/api/embed/updateEmbed', payload)
    .then(() => {
      return 'Embed updated successfully'
    })
    .catch(() => {
      throw new Error('Failed to update embed details')
    })
}

/**
 * Set the account type to the store
 * @param data - The account type
 */
const setCurrentEmbedsTypeCount = (data: { [key: string]: number }) => {
  useEmbedConfigureStore().setCurrentEmbedsTypeCount(data)
}
/**
 * Get the default config
 * @param data - The embed
 * @returns - The default config
 */
const getDefaultConfig = (data: IEmbed | null = null) => {
  let languages: IEmbedLanguage[] = [] as IEmbedLanguage[]
  if (data) {
    if (data?.draft && data?.draft?.languages?.length > 0)
      languages = [cloneDeep(data.draft.languages[0])]
    if (data?.config?.languages?.length > 0) languages = [cloneDeep(data.config.languages[0])]
  }
  const config = { ...CONFIG }

  const componentSettings: IComponentSettings[] = cloneDeep(COMPONENTSETTINGS)

  const defaultConfig = {
    languages,
    config,
    componentSettings,
    defaultConfig: {
      stdinArgs: '',
      inputArgs: '',
      compilerArgs: '',
      interActiveMode: false,
      fontSize: 12
    },
    inputFiles: [],
    html: HTML,
    css: CSS,
    js: JS
  }
  return defaultConfig
}
/**
 * Set the embed to the store
 * @param id - The embed id
 * @param data - The embed
 */
const setEmbed = (id?: string | null, data?: IEmbed) => {
  const embed: IEmbed = {
    id: id || null,
    embedKey: data?.embedKey || null,
    name: data?.name || '',
    description: data?.description || '',
    type: data?.type || EMBEDTYPES.BASIC,
    draft: data?.draft || null,
    config: data
      ? data.type === EMBEDTYPES.BASIC
        ? getDefaultConfig(data)
        : data.config
      : getDefaultConfig()
  }

  if (embed.draft === null || embed.type === EMBEDTYPES.BASIC) {
    //remove html and css from config
    embed.draft = cloneDeep(
      pick(embed.config, [
        'languages',
        'config',
        'componentSettings',
        'defaultConfig',
        'inputFiles'
      ])
    )
  }
  useEmbedConfigureStore().setEmbed(embed)
}
/**
 * Fetch the embed by id
 * @param projectId - The project id or null
 * @param projectLanguage - The project language or null
 * @returns - promise
 */
const fetchEmbedProjectId = async (projectId: number, projectLanguage: string) => {
  return await getEmbedsTypeCountData()
    .then(async () => {
      const payload: any = {
        id: projectId,
        lang: projectLanguage,
        isMultiFile: false
      }
      return await axios
        .post('/api/doodle/file', payload)
        .then((response: { data: any }) => {
          const data = response.data?.project
          if (!data || !data.language) {
            throw new Error('Failed to get project details. Please refresh the page and try again.')
          }
          if (data.isMultiFile === true) {
            throw new Error('Advance projects are not supported.')
          }
          if (data.language === 'html' || data.language === 'blockly') {
            throw new Error('This language is not supported.')
          }
          const currentEmbed = useEmbedConfigureStore().getEmbed
          currentEmbed.name = data.name || ''
          currentEmbed.description = `Embed created from project ${data.name}`

          const languageAndVersion: IEmbedLanguage = {
            language: data.language || 'java',
            versionIndex: data.versionIndex || 0,
            theme: 'xcode',
            program: JSON.stringify(data.script) || '',
            libs:
              (data.libraries ? data.libraries.split(' ').map((lib: string) => lib.trim()) : []) ||
              []
          }
          useEmbedConfigureStore().addLanguageToEmbed(languageAndVersion)
        })
        .catch((err) => {
          const message = 'Failed to get project details. Please refresh the page and try again.'
          throw new Error(
            err.message !== 'Request failed with status code 400' ? err.message : message || message
          )
        })
    })
    .catch((err) => {
      throw new Error(
        err.message ||
          'Failed to initialize the embed configuration. Please refresh the page and try again.'
      )
    })
}
/**
 * Fetch the embed by id
 * @param embedId - The embed id or null
 * @returns - promise
 */
const fetchEmbedById = async (embedId: string) => {
  const isOneditMode = useEmbedConfigureStore().getOnEditMode

  let payload: any | null = null
  let url = ''
  if (isOneditMode) {
    payload = {
      id: embedId
    }
    url = '/api/embed/getEmbed'
  } else {
    url = `/api/embed/initEmbed?embedKey=${embedId}`
  }
  await axios
    .post(url, payload)
    .then(async (response: any) => {
      if (!response.data || (isOneditMode && !response.data.embedTypeCountDTOs)) {
        throw new Error('Failed to get embed details. Please refresh the page and try again.')
      }

      if (isOneditMode) {
        const embedsTypeCountData = {
          [EMBEDTYPES.BASIC]: 0,
          [EMBEDTYPES.PREMIUM]: 0,
          [EMBEDTYPES.ULTRA]: 0
        }
        response.data.embedTypeCountDTOs.forEach((embedTypeCountDTO: any) => {
          embedsTypeCountData[embedTypeCountDTO.embedType] = embedTypeCountDTO.number
        })
        setCurrentEmbedsTypeCount(embedsTypeCountData)
      }
      const embedData: IEmbed = {
        id: response.data.id,
        embedKey: response.data.embedKey,
        name: response.data.name,
        description: response.data.description,
        type: response.data.embedType,
        draft: response.data.draft,
        config: response.data.config
      }
      if (embedData.id) {
        setEmbed(embedData.id, embedData)
        await new Promise((resolve) => setTimeout(resolve, 100))
        await uploadService.UpdateDefaultInputFiles(embedData.embedKey as string)
      } else {
        throw new Error('Failed to get embed details. Please refresh the page and try again.')
      }
    })
    .catch((err) => {
      throw new Error(
        err.message || 'Failed to get embed details. Please refresh the page and try again.'
      )
    })
}
/**
 * Create new embed
 * @returns - promise
 */
const createNewEmbed = async () => {
  const currentEmbed = useEmbedConfigureStore().getEmbed
  const newConfig: IEmbedConfigConig = cloneDeep(currentEmbed.draft) as IEmbedConfigConig

  const html = useEmbedConfigureStore().htmlRef
  const regex = /<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi
  const cleanedHtml = html.replace(regex, '')
  const css = useEmbedConfigureStore().cssRef
  const js = useEmbedConfigureStore().jsRef
  newConfig.html = cleanedHtml || HTML
  newConfig.css = css || CSS
  newConfig.js = js || JS

  const payload = {
    name: currentEmbed.name,
    description: currentEmbed.description,
    type: currentEmbed.type,
    draft: currentEmbed.draft,
    config: cloneDeep(newConfig)
  }
  return await axios
    .post('/api/embed/createEmbed', payload)
    .then((response: any) => {
      const embedId = response.data.id
      const embedKey = response.data.embedKey
      const currentEmbed = useEmbedConfigureStore().getEmbed
      currentEmbed.embedKey = embedKey

      const draftLanguages: IEmbedLanguage[] = currentEmbed.draft?.languages as IEmbedLanguage[]
      currentEmbed.config.languages = cloneDeep(draftLanguages) || []

      setEmbed(embedId, currentEmbed)
      const isNewEmbed = useEmbedConfigureStore().getIsNewEmbed
      if (isNewEmbed) useEmbedConfigureStore().setIsNewEmbed(false)
      return response
    })
    .catch((err) => {
      // check err?.response?.data?.errors[0] has Embed name already exists
      if (
        err?.response?.data?.errors[0] &&
        err?.response?.data?.errors[0].includes('Embed name already exists"')
      ) {
        throw new Error('Embed name already exists. Please use a different name.')
      } else throw new Error('Failed to create new embed. Please try again.')
    })
}
/**
 * Fetch the embeds type count
 * @returns - promise
 */
const getEmbedsTypeCountData = async () => {
  return await axios
    .post('/api/embed/getEmbedCountByEmbedType')
    .then((response: any) => {
      if (!response.data) {
        throw new Error(
          'Failed to initialize the embed configuration. Please refresh the page and try again.'
        )
      }
      const embedsTypeCountData = {
        [EMBEDTYPES.BASIC]: 0,
        [EMBEDTYPES.PREMIUM]: 0,
        [EMBEDTYPES.ULTRA]: 0
      }
      response.data.forEach((embedTypeCountDTO: any) => {
        embedsTypeCountData[embedTypeCountDTO.embedType] = embedTypeCountDTO.number
      })

      setCurrentEmbedsTypeCount(embedsTypeCountData)
    })
    .catch((err) => {
      throw new Error(
        err.message ||
          'Failed to initialize the embed configuration. Please refresh the page and try again.'
      )
    })
}
/**
 * Initialize the embed configure store
 * @param embedId - The embed id
 * @param projectId - The project id
 * @param projectLanguage - The project language
 * @returns - promise
 */
const initEmbedConfigure = async (
  embedId: string | null = null,
  projectId: number | null = null,
  projectLanguage: string | null = null
) => {
  // reset the store
  useEmbedConfigureStore().$reset()
  // set to default embed
  setEmbed()
  // set the edit mode to true
  useEmbedConfigureStore().setOnEditMode(true)
  // set is new embed or not based on embed id
  useEmbedConfigureStore().setIsNewEmbed(embedId === null)
  // fetch the embed details and embeds count
  if (useIdeStore().isIdeView || useIdeStore().isDefaultIde) {
    const currentEmbed = useEmbedConfigureStore().getEmbed
    currentEmbed.name =
      useIdeStore().project.name + '-' + uniqueNamesGenerator({ dictionaries: [animals] }) || ''
    currentEmbed.description = `Embed created from project ${useIdeStore().project.name || ''}`
    const languageAndVersion: IEmbedLanguage = {
      language: useIdeStore().isLanguage || 'java',
      versionIndex: useIdeStore().versionIndex || 0,
      theme: 'xcode',
      program:
        JSON.stringify(editorService.getEditorSession(IDECONSTANT.CODE_EDITOR).getValue()) || '',
      libs:
        (useIdeStore().libraries ? useIdeStore().libraries.map((lib: string) => lib.trim()) : []) ||
        []
    }
    useEmbedConfigureStore().addLanguageToEmbed(languageAndVersion)

    return createNewEmbed()
      .then((embedResponse) => {
        return embedResponse?.data?.embedKey
      })
      .finally(() => {
        useEmbedConfigureStore().$reset()
      })
  } else {
    if (useEmbedConfigureStore().getIsNewEmbed) {
      if (projectId !== null && projectLanguage !== null) {
        return await fetchEmbedProjectId(projectId as number, projectLanguage as string)
      } else return await getEmbedsTypeCountData()
    } else return await fetchEmbedById(embedId as string)
  }
}
export default {
  setEmbed,
  saveDraft,
  saveConfig,
  initEmbedConfigure,
  createNewEmbed,
  fetchEmbedById
}
